import { Link } from "react-router-dom";
import { Check, ArrowUpRight } from "lucide-react";
import { FadeIn, Stagger, StaggerItem } from "../motion";
import { SERVICES, CATEGORIES, IMAGES } from "../../data/content";

const ServiceFeatures = ({ features }) => features ? (
  <ul className="mt-6 grid sm:grid-cols-2 gap-4 max-w-2xl">{features.map((feature) => <li key={feature} className="flex items-center gap-3 text-sm font-semibold text-[#811F5D] bg-[#811F5D]/5 px-4 py-2.5 rounded-xl"><Check size={16} className="text-[#D4AF37] shrink-0" />{feature}</li>)}</ul>
) : null;

export const ServicesList = () => (
  <section className="bg-gradient-to-b from-[#FAF7F2] to-[#F3EEE5] py-24 sm:py-32" data-testid="services-list-section">
    <div className="max-w-7xl mx-auto px-5 sm:px-8 space-y-10">
      {SERVICES.map((service, position) => (
        <FadeIn key={service.slug} delay={0.03 * position}>
          <div id={service.slug} className="group scroll-mt-28 bg-white rounded-3xl border border-[#811F5D]/10 p-8 lg:p-12 grid lg:grid-cols-[120px_1fr] gap-8 transition-all duration-500 hover:border-[#811F5D]/30 hover:shadow-[0_30px_60px_rgba(74,17,54,0.08)] hover:-translate-y-1.5" data-testid={`service-detail-${service.slug}`}>
            <span className="font-heading font-extrabold text-6xl bg-gradient-to-br from-[#E5C158] to-[#B29235] bg-clip-text text-transparent transition-transform duration-500 group-hover:scale-105 select-none">{service.num}</span>
            <div>
              <h2 className="font-heading font-extrabold text-[#2A0E20] text-2xl sm:text-3xl tracking-tight transition-colors duration-300 group-hover:text-[#811F5D]">{service.title}</h2>
              <div className="w-12 h-[3px] bg-[#D4AF37] my-5 transition-all duration-500 group-hover:w-24" />
              <p className="mt-4 text-[#4B3A45] leading-relaxed max-w-4xl text-base font-medium">{service.full}</p>
              <ServiceFeatures features={service.features} />
            </div>
          </div>
        </FadeIn>
      ))}
    </div>
  </section>
);

export const CategoriesSection = () => (
  <section className="bg-[#4A1136] relative overflow-hidden" data-testid="categories-section">
    <div className="absolute inset-0 bg-gradient-to-r from-[#4A1136] via-[#6B184D] to-[#4A1136] opacity-95" />
    <div className="absolute inset-0 hero-pattern opacity-10" />
    <div className="max-w-7xl mx-auto px-5 sm:px-8 py-24 relative z-10">
      <FadeIn className="mb-14 flex flex-wrap items-end justify-between gap-6">
        <div>
          <p className="font-mono text-xs tracking-[0.35em] uppercase text-[#D4AF37] mb-4">Working Categories</p>
          <h2 className="font-heading font-extrabold text-white text-3xl sm:text-4xl tracking-tight leading-tight">
            11 verticals. White-collar<br />to blue-collar. <span className="text-[#D4AF37]">Covered.</span>
          </h2>
        </div>
        <img src={IMAGES.industrial} alt="Industrial workforce" className="w-64 h-36 object-cover rounded-2xl border border-[#D4AF37]/35 hidden md:block shadow-2xl" />
      </FadeIn>
      <Stagger className="flex flex-wrap gap-4">
        {CATEGORIES.map((category) => (
          <StaggerItem key={category}>
            <span className="inline-block border border-white/20 bg-white/5 text-white/90 px-6 py-4 text-sm font-semibold transition-all duration-300 hover:bg-gradient-to-r hover:from-[#E5C158] hover:to-[#B29235] hover:text-[#2A0E20] hover:border-transparent active:scale-95 cursor-default select-none shadow-md rounded-2xl" data-testid={`category-chip-${category.toLowerCase().replaceAll(" ", "-").replaceAll("&", "and")}`}>
              {category}
            </span>
          </StaggerItem>
        ))}
      </Stagger>
    </div>
  </section>
);

export const ServicesCta = () => (
  <section className="bg-gradient-to-b from-[#FAF7F2] to-[#F3EEE5] border-t border-[#811F5D]/8" data-testid="services-cta-section">
    <div className="max-w-7xl mx-auto px-5 sm:px-8 py-20 flex flex-wrap items-center justify-between gap-10">
      <FadeIn className="max-w-2xl">
        <h2 className="font-heading font-extrabold text-[#2A0E20] text-3xl sm:text-4xl tracking-tight leading-tight">
          Tell us your manpower requirement — get a response within <span className="text-[#811F5D]">1 working day.</span>
        </h2>
      </FadeIn>
      <FadeIn delay={0.12}>
        <Link to="/contact" className="btn-shine inline-flex items-center gap-2 bg-[#811F5D] text-white px-10 py-5 font-extrabold tracking-wider transition-all duration-300 hover:bg-[#4A1136] hover:gap-4 hover:shadow-2xl active:scale-[0.97] rounded-2xl shadow-xl focus:outline-none focus:ring-2 focus:ring-[#D4AF37] focus:ring-offset-2" data-testid="services-cta-link">
          Submit a requirement <ArrowUpRight size={18} />
        </Link>
      </FadeIn>
    </div>
  </section>
);